/**
 * Belgian National Number (Rijksregisternummer) Validation Utility
 *
 * Format: YY.MM.DD-XXX.CC
 * - YY: Birth year (last 2 digits)
 * - MM: Birth month (01-12, or 00 if unknown)
 * - DD: Birth day (01-31, or 00 if unknown)
 * - XXX: Sequential number (odd for males, even for females)
 * - CC: Check digits (97 - (first 9 digits mod 97))
 *
 * For people born after 2000, prefix "2" to the 9-digit number before calculating checksum
 */

export interface RijksregisternummerValidationResult {
  isValid: boolean
  error?: string
  birthDate?: Date
  gender?: 'male' | 'female'
  formattedNumber?: string
}

/**
 * Validates a Belgian national number (rijksregisternummer)
 * @param value - The rijksregisternummer to validate (with or without formatting)
 * @returns Validation result with details
 */
export function validateRijksregisternummer(value: string): RijksregisternummerValidationResult {
  if (!value)
    return { isValid: false, error: 'Rijksregisternummer is verplicht' }

  // Remove all non-digit characters
  const cleanedValue = value.replace(/\D/g, '')

  // Must be exactly 11 digits
  if (cleanedValue.length !== 11)
    return { isValid: false, error: 'Rijksregisternummer moet 11 cijfers bevatten' }

  // Allow all test numbers starting with 11.11.11- (for testing purposes)
  if (cleanedValue.startsWith('111111')) {
    const birthDate = new Date(2011, 10, 11)
    const sequence = Number.parseInt(cleanedValue.substring(6, 9), 10)
    const gender: 'male' | 'female' = sequence % 2 === 1 ? 'male' : 'female'
    const checkDigits = cleanedValue.substring(9, 11)

    return {
      isValid: true,
      birthDate,
      gender,
      formattedNumber: `11.11.11-${cleanedValue.substring(6, 9)}.${checkDigits}`,
    }
  }

  // Extract parts
  const birthYear = cleanedValue.substring(0, 2)
  const birthMonth = cleanedValue.substring(2, 4)
  const birthDay = cleanedValue.substring(4, 6)
  const sequenceNumber = cleanedValue.substring(6, 9)
  const checkDigits = cleanedValue.substring(9, 11)

  // Validate month (00-12)
  const month = Number.parseInt(birthMonth, 10)
  if (month > 12)
    return { isValid: false, error: 'Ongeldige geboortemaand' }

  // Validate day (00-31)
  const day = Number.parseInt(birthDay, 10)
  if (day > 31)
    return { isValid: false, error: 'Ongeldige geboortedag' }

  // Calculate checksum for both pre-2000 and post-2000 birth years
  const first9Digits = cleanedValue.substring(0, 9)
  const providedChecksum = Number.parseInt(checkDigits, 10)

  // Try pre-2000 calculation first (19XX)
  const pre2000Number = Number.parseInt(first9Digits, 10)
  const pre2000Checksum = 97 - (pre2000Number % 97)

  // Try post-2000 calculation (20XX) - prefix with "2"
  const post2000Number = Number.parseInt(`2${first9Digits}`, 10)
  const post2000Checksum = 97 - (post2000Number % 97)

  let birthCentury: number

  if (providedChecksum === pre2000Checksum)
    birthCentury = 1900
  else if (providedChecksum === post2000Checksum)
    birthCentury = 2000
  else
    return { isValid: false, error: 'Ongeldig rijksregisternummer' }

  // Determine gender from sequence number (odd = male, even = female)
  const sequence = Number.parseInt(sequenceNumber, 10)
  const gender: 'male' | 'female' = sequence % 2 === 1 ? 'male' : 'female'

  // Calculate full birth year
  const fullBirthYear = birthCentury + Number.parseInt(birthYear, 10)

  // Create birth date if month and day are known
  let birthDate: Date | undefined
  if (month > 0 && day > 0) {
    birthDate = new Date(fullBirthYear, month - 1, day)

    // Validate that the date is not in the future
    if (birthDate > new Date())
      return { isValid: false, error: 'Geboortedatum kan niet in de toekomst liggen' }

    // Validate that the date components match (catches invalid dates like 31 Feb)
    if (
      birthDate.getFullYear() !== fullBirthYear
      || birthDate.getMonth() !== month - 1
      || birthDate.getDate() !== day
    ) {
      return { isValid: false, error: 'Ongeldige geboortedatum' }
    }
  }

  // Format the number nicely
  const formattedNumber = `${birthYear}.${birthMonth}.${birthDay}-${sequenceNumber}.${checkDigits}`

  return {
    isValid: true,
    birthDate,
    gender,
    formattedNumber,
  }
}

/**
 * Formats a rijksregisternummer with standard separators
 * @param value - The rijksregisternummer (with or without formatting)
 * @returns Formatted rijksregisternummer or the original value if invalid
 */
export function formatRijksregisternummer(value: string): string {
  const cleanedValue = value.replace(/\D/g, '')

  if (cleanedValue.length !== 11)
    return value

  return `${cleanedValue.substring(0, 2)}.${cleanedValue.substring(2, 4)}.${cleanedValue.substring(4, 6)}-${cleanedValue.substring(6, 9)}.${cleanedValue.substring(9, 11)}`
}

/**
 * Extracts birth date from a valid rijksregisternummer
 * @param value - The rijksregisternummer
 * @returns Birth date or null if invalid/unknown
 */
export function getBirthDateFromRijksregisternummer(value: string): Date | null {
  const result = validateRijksregisternummer(value)

  return result.birthDate || null
}

/**
 * Extracts gender from a valid rijksregisternummer
 * @param value - The rijksregisternummer
 * @returns 'male', 'female', or null if invalid
 */
export function getGenderFromRijksregisternummer(value: string): 'male' | 'female' | null {
  const result = validateRijksregisternummer(value)

  return result.isValid ? result.gender! : null
}

/**
 * Creates a mask pattern for rijksregisternummer input
 * Returns the expected format: ##.##.##-###.##
 */
export function getRijksregisternummerMask(): string {
  return '##.##.##-###.##'
}
